import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // This error only fires in development when env vars are missing - keep visible
  if (import.meta.env.DEV) {
    console.error('Environment variables check:', {
      VITE_SUPABASE_URL: supabaseUrl,
      VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? '***' : undefined,
      allEnvVars: import.meta.env
    });
  }
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================
// Onboarding Types
// ============================================

/** Valid user segments selected during onboarding */
export type UserSegment = 'agent' | 'photographer' | 'brokerage' | 'fsbo' | 'homeowner' | 'other';

/** Monthly property volume ranges for pro users */
export type MonthlyPropertyVolume = '1-3' | '4-10' | '11-25' | '26+';

/** Primary pain points for pro users */
export type PrimaryPainPoint = 'visualization' | 'cost' | 'speed' | 'competition' | 'differentiation';

/** Acquisition channels */
export type AcquisitionChannel =
  | 'google_search'
  | 'facebook_instagram_ad'
  | 'referral'
  | 'conference_event'
  | 'youtube'
  | 'podcast'
  | 'other';

/** Consumer timeline options */
export type ConsumerTimeline = 'this_week' | 'this_month' | 'exploring';

/** Onboarding step names for progress tracking */
export type OnboardingStep =
  | 'welcome'
  | 'questions'
  | 'stage'
  | 'result'
  | 'personalize'
  | 'value'
  | 'success';

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

type JsonObject = { [key: string]: JsonValue };

/**
 * JSONB structure for onboarding_data column
 * Contains all collected data and progress state during onboarding
 */
export interface OnboardingData {
  // Session tracking
  session_id?: string;
  started_at?: string;
  completed_at?: string;

  // Attribution (captured from URL params on entry)
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer_url?: string;
  device_type?: 'desktop' | 'mobile' | 'tablet';

  // Pro questions (for agent/photographer/brokerage segments)
  monthly_property_volume?: MonthlyPropertyVolume;
  primary_pain_point?: PrimaryPainPoint;
  skipped_pro_questions?: boolean;

  // Consumer questions (for fsbo/homeowner segments)
  first_room_type_selected?: string;
  consumer_timeline?: ConsumerTimeline;

  // Staging data
  first_photo_uploaded_at?: string;
  used_sample_photo?: boolean;
  first_style_selected?: string;
  first_room_type_staged?: string;
  staging_completed_at?: string;
  time_to_first_stage_seconds?: number;
  first_staged_image_id?: string;
  first_project_id?: string;

  // Reveal actions
  downloaded_first_photo?: boolean;
  tried_another_style?: boolean;
  shared_first_photo?: boolean;

  // Personalization
  mls_market?: string;
  brokerage_name?: string;
  acquisition_channel?: AcquisitionChannel;

  // ROI Bridge (free users)
  viewed_roi_calculator?: boolean;
  roi_calculator_value?: number;
  clicked_view_plans?: boolean;
  clicked_continue_free?: boolean;

  // Progress tracking
  last_completed_step?: OnboardingStep;
  steps_completed?: OnboardingStep[];
  drop_off_step?: OnboardingStep;

  // Backfill flag (for existing users)
  backfilled?: boolean;
  backfilled_at?: string;
  reason?: string;
}

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          email: string;
          full_name: string;
          company_name: string | null;
          credits: number;
          phone: string | null;
          license_number: string | null;
          brokerage: string | null;
          years_experience: number | null;
          bio: string | null;
          tagline: string | null;
          specialties: string | null;
          areas_served: string | null;
          website: string | null;
          linkedin_url: string | null;
          instagram_url: string | null;
          facebook_url: string | null;
          profile_photo_url: string | null;
          created_at: string;
          updated_at: string;
          /** User segment selected during onboarding */
          user_segment: UserSegment | null;
          /** Whether onboarding has been completed */
          onboarding_completed: boolean;
          /** Timestamp when onboarding was completed */
          onboarding_completed_at: string | null;
          /** JSONB store for onboarding progress and data */
          onboarding_data: OnboardingData;
        };
        Insert: {
          id: string;
          email: string;
          full_name: string;
          company_name?: string | null;
          credits?: number;
          phone?: string | null;
          license_number?: string | null;
          brokerage?: string | null;
          years_experience?: number | null;
          bio?: string | null;
          tagline?: string | null;
          specialties?: string | null;
          areas_served?: string | null;
          website?: string | null;
          linkedin_url?: string | null;
          instagram_url?: string | null;
          facebook_url?: string | null;
          profile_photo_url?: string | null;
          created_at?: string;
          updated_at?: string;
          user_segment?: UserSegment | null;
          onboarding_completed?: boolean;
          onboarding_completed_at?: string | null;
          onboarding_data?: OnboardingData;
        };
        Update: {
          id?: string;
          email?: string;
          full_name?: string;
          company_name?: string | null;
          credits?: number;
          phone?: string | null;
          license_number?: string | null;
          brokerage?: string | null;
          years_experience?: number | null;
          bio?: string | null;
          tagline?: string | null;
          specialties?: string | null;
          areas_served?: string | null;
          website?: string | null;
          linkedin_url?: string | null;
          instagram_url?: string | null;
          facebook_url?: string | null;
          profile_photo_url?: string | null;
          created_at?: string;
          updated_at?: string;
          user_segment?: UserSegment | null;
          onboarding_completed?: boolean;
          onboarding_completed_at?: string | null;
          onboarding_data?: OnboardingData;
        };
      };
      projects: {
        Row: {
          id: string;
          user_id: string;
          title: string;
          property_address: string | null;
          description: string | null;
          status: 'draft' | 'in_progress' | 'completed';
          import_source: 'zillow' | 'streeteasy' | 'redfin' | 'realtor' | 'mls' | 'other' | null;
          import_url: string | null;
          import_metadata: Record<string, unknown> | null;
          imported_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          title: string;
          property_address?: string | null;
          description?: string | null;
          status?: 'draft' | 'in_progress' | 'completed';
          import_source?: 'zillow' | 'streeteasy' | 'redfin' | 'realtor' | 'mls' | 'other' | null;
          import_url?: string | null;
          import_metadata?: Record<string, unknown> | null;
          imported_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          title?: string;
          property_address?: string | null;
          description?: string | null;
          status?: 'draft' | 'in_progress' | 'completed';
          import_source?: 'zillow' | 'streeteasy' | 'redfin' | 'realtor' | 'mls' | 'other' | null;
          import_url?: string | null;
          import_metadata?: Record<string, unknown> | null;
          imported_at?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      images: {
        Row: {
          id: string;
          project_id: string;
          user_id: string;
          original_url: string;
          filename: string;
          /** References the parent image if this was derived from decluttering */
          source_image_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          project_id: string;
          user_id: string;
          original_url: string;
          filename: string;
          /** References the parent image if this was derived from decluttering */
          source_image_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          project_id?: string;
          user_id?: string;
          original_url?: string;
          filename?: string;
          /** References the parent image if this was derived from decluttering */
          source_image_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      staged_images: {
        Row: {
          id: string;
          image_id: string;
          staged_url: string;
          /** Style slug (e.g., 'modern', 'scandinavian') */
          style: string | null;
          /** Reference to base_styles table */
          base_style_id: string | null;
          /** Reference to user_styles table */
          user_style_id: string | null;
          /** Structure domain: 'interior' or 'exterior' */
          structure_domain: 'interior' | 'exterior' | null;
          /** Structure category (e.g., 'living_spaces', 'bedrooms') */
          structure_category: string | null;
          /** Structure type (e.g., 'living_room', 'primary_bedroom') */
          structure_type: string | null;
          /** Complete prompt used for staging */
          full_prompt: string | null;
          /** Prompt version for tracking */
          prompt_version: string;
          /** Processing status */
          status: 'pending' | 'processing' | 'completed' | 'failed';
          processing_started_at: string | null;
          processing_completed_at: string | null;
          error_message: string | null;
          created_at: string;
          /** Prompt bundle version for V3 observability */
          prompt_bundle_version: string | null;
          /** SHA-256 hash of the full prompt */
          prompt_hash: string | null;
        };
        Insert: {
          id?: string;
          image_id: string;
          staged_url: string;
          style?: string | null;
          base_style_id?: string | null;
          user_style_id?: string | null;
          structure_domain?: 'interior' | 'exterior' | null;
          structure_category?: string | null;
          structure_type?: string | null;
          full_prompt?: string | null;
          prompt_version?: string;
          status?: 'pending' | 'processing' | 'completed' | 'failed';
          processing_started_at?: string | null;
          processing_completed_at?: string | null;
          error_message?: string | null;
          created_at?: string;
          prompt_bundle_version?: string | null;
          prompt_hash?: string | null;
        };
        Update: {
          id?: string;
          image_id?: string;
          staged_url?: string;
          style?: string | null;
          base_style_id?: string | null;
          user_style_id?: string | null;
          structure_domain?: 'interior' | 'exterior' | null;
          structure_category?: string | null;
          structure_type?: string | null;
          full_prompt?: string | null;
          prompt_version?: string;
          status?: 'pending' | 'processing' | 'completed' | 'failed';
          processing_started_at?: string | null;
          processing_completed_at?: string | null;
          error_message?: string | null;
          created_at?: string;
          prompt_bundle_version?: string | null;
          prompt_hash?: string | null;
        };
      };
      staging_jobs: {
        Row: {
          id: string;
          user_id: string;
          image_id: string;
          status: 'queued' | 'processing' | 'completed' | 'failed' | 'canceled';
          idempotency_key: string;
          request_payload: Record<string, unknown>;
          attempts: number;
          last_error: string | null;
          result_staged_image_id: string | null;
          queued_at: string;
          started_at: string | null;
          completed_at: string | null;
          priority: number | null;
          credit_transaction_id: string | null;
          credit_usage_id: string | null;
          credit_source: 'subscription' | 'profile' | null;
        };
        Insert: {
          id?: string;
          user_id: string;
          image_id: string;
          status?: 'queued' | 'processing' | 'completed' | 'failed' | 'canceled';
          idempotency_key: string;
          request_payload?: Record<string, unknown>;
          attempts?: number;
          last_error?: string | null;
          result_staged_image_id?: string | null;
          queued_at?: string;
          started_at?: string | null;
          completed_at?: string | null;
          priority?: number | null;
          credit_transaction_id?: string | null;
          credit_usage_id?: string | null;
          credit_source?: 'subscription' | 'profile' | null;
        };
        Update: {
          id?: string;
          user_id?: string;
          image_id?: string;
          status?: 'queued' | 'processing' | 'completed' | 'failed' | 'canceled';
          idempotency_key?: string;
          request_payload?: Record<string, unknown>;
          attempts?: number;
          last_error?: string | null;
          result_staged_image_id?: string | null;
          queued_at?: string;
          started_at?: string | null;
          completed_at?: string | null;
          priority?: number | null;
          credit_transaction_id?: string | null;
          credit_usage_id?: string | null;
          credit_source?: 'subscription' | 'profile' | null;
        };
      };
      staged_image_feedback: {
        Row: {
          id: string;
          staged_image_id: string | null;
          image_id: string;
          user_id: string;
          rating: -1 | 0 | 1;
          reason: 'wrong_style' | 'objects_look_off' | 'missing_furniture' | 'other' | null;
          notes: string | null;
          wants_support: boolean | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          staged_image_id?: string | null;
          image_id: string;
          user_id: string;
          rating: -1 | 0 | 1;
          reason?: 'wrong_style' | 'objects_look_off' | 'missing_furniture' | 'other' | null;
          notes?: string | null;
          wants_support?: boolean | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          staged_image_id?: string | null;
          image_id?: string;
          user_id?: string;
          rating?: -1 | 0 | 1;
          reason?: 'wrong_style' | 'objects_look_off' | 'missing_furniture' | 'other' | null;
          notes?: string | null;
          wants_support?: boolean | null;
          created_at?: string;
        };
      };
      support_tickets: {
        Row: {
          id: string;
          user_id: string;
          image_id: string | null;
          staged_image_id: string | null;
          feedback_id: string | null;
          status: 'open' | 'in_progress' | 'resolved' | 'closed';
          resolution: 'credit_granted' | 'no_action' | 'goodwill_discount' | null;
          resolution_notes: string | null;
          credits_granted: number;
          resolved_at: string | null;
          resolved_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          image_id?: string | null;
          staged_image_id?: string | null;
          feedback_id?: string | null;
          status?: 'open' | 'in_progress' | 'resolved' | 'closed';
          resolution?: 'credit_granted' | 'no_action' | 'goodwill_discount' | null;
          resolution_notes?: string | null;
          credits_granted?: number;
          resolved_at?: string | null;
          resolved_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          staged_image_id?: string | null;
          user_id?: string;
          image_id?: string | null;
          feedback_id?: string | null;
          status?: 'open' | 'in_progress' | 'resolved' | 'closed';
          resolution?: 'credit_granted' | 'no_action' | 'goodwill_discount' | null;
          resolution_notes?: string | null;
          credits_granted?: number;
          resolved_at?: string | null;
          resolved_by?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      credit_transactions: {
        Row: {
          id: string;
          user_id: string;
          amount: number;
          transaction_type: 
            | 'staging_used' 
            | 'subscription_grant' 
            | 'bonus' 
            | 'purchase' 
            | 'support_grant'
            | 'refund'
            | 'subscription_payment'
            | 'one_time_payment'
            | 'subscription_created'
            | 'subscription_updated'
            | 'subscription_cancelled'
            | 'subscription_resumed'
            | 'subscription_upgraded'
            | 'subscription_downgraded'
            | 'subscription_renewed';
          transaction_category: 'credit' | 'payment' | 'subscription' | 'refund';
          subscription_id: string | null;
          invoice_id: string | null;
          stripe_payment_intent_id: string | null;
          stripe_charge_id: string | null;
          stripe_refund_id: string | null;
          amount_cents: number | null;
          currency: string | null;
          status: 'pending' | 'completed' | 'failed' | 'refunded';
          metadata: JsonObject | null;
          image_id: string | null;
          description: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          amount: number;
          transaction_type: 
            | 'staging_used' 
            | 'subscription_grant' 
            | 'bonus' 
            | 'purchase' 
            | 'support_grant'
            | 'refund'
            | 'subscription_payment'
            | 'one_time_payment'
            | 'subscription_created'
            | 'subscription_updated'
            | 'subscription_cancelled'
            | 'subscription_resumed'
            | 'subscription_upgraded'
            | 'subscription_downgraded'
            | 'subscription_renewed';
          transaction_category: 'credit' | 'payment' | 'subscription' | 'refund';
          subscription_id?: string | null;
          invoice_id?: string | null;
          stripe_payment_intent_id?: string | null;
          stripe_charge_id?: string | null;
          stripe_refund_id?: string | null;
          amount_cents?: number | null;
          currency?: string | null;
          status?: 'pending' | 'completed' | 'failed' | 'refunded';
          metadata?: JsonObject | null;
          image_id?: string | null;
          description: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          amount?: number;
          transaction_type?: 
            | 'staging_used' 
            | 'subscription_grant' 
            | 'bonus' 
            | 'purchase' 
            | 'support_grant'
            | 'refund'
            | 'subscription_payment'
            | 'one_time_payment'
            | 'subscription_created'
            | 'subscription_updated'
            | 'subscription_cancelled'
            | 'subscription_resumed'
            | 'subscription_upgraded'
            | 'subscription_downgraded'
            | 'subscription_renewed';
          transaction_category?: 'credit' | 'payment' | 'subscription' | 'refund';
          subscription_id?: string | null;
          invoice_id?: string | null;
          stripe_payment_intent_id?: string | null;
          stripe_charge_id?: string | null;
          stripe_refund_id?: string | null;
          amount_cents?: number | null;
          currency?: string | null;
          status?: 'pending' | 'completed' | 'failed' | 'refunded';
          metadata?: JsonObject | null;
          image_id?: string | null;
          description?: string;
          created_at?: string;
        };
      };
      subscription_plans: {
        Row: {
          id: string;
          name: string;
          slug: string;
          description: string | null;
          monthly_price_cents: number;
          annual_price_cents: number;
          credits_per_month: number;
          features: string[] | null;
          is_active: boolean;
          display_order: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          slug: string;
          description?: string | null;
          monthly_price_cents: number;
          annual_price_cents: number;
          credits_per_month: number;
          features?: string[] | null;
          is_active?: boolean;
          display_order?: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          slug?: string;
          description?: string | null;
          monthly_price_cents?: number;
          annual_price_cents?: number;
          credits_per_month?: number;
          features?: string[] | null;
          is_active?: boolean;
          display_order?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_subscriptions: {
        Row: {
          id: string;
          user_id: string;
          plan_id: string;
          status: 'active' | 'cancelled' | 'expired' | 'past_due' | 'trial';
          billing_cycle: 'monthly' | 'annual';
          current_period_start: string;
          current_period_end: string;
          cancel_at_period_end: boolean;
          scheduled_cancellation_date: string | null;
          cancelled_at: string | null;
          trial_start: string | null;
          trial_end: string | null;
          stripe_customer_id: string | null;
          stripe_subscription_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          plan_id: string;
          status: 'active' | 'cancelled' | 'expired' | 'past_due' | 'trial';
          billing_cycle: 'monthly' | 'annual';
          current_period_start: string;
          current_period_end: string;
          cancel_at_period_end?: boolean;
          scheduled_cancellation_date?: string | null;
          cancelled_at?: string | null;
          trial_start?: string | null;
          trial_end?: string | null;
          stripe_customer_id?: string | null;
          stripe_subscription_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          plan_id?: string;
          status?: 'active' | 'cancelled' | 'expired' | 'past_due' | 'trial';
          billing_cycle?: 'monthly' | 'annual';
          current_period_start?: string;
          current_period_end?: string;
          cancel_at_period_end?: boolean;
          scheduled_cancellation_date?: string | null;
          cancelled_at?: string | null;
          trial_start?: string | null;
          trial_end?: string | null;
          stripe_customer_id?: string | null;
          stripe_subscription_id?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      subscription_invoices: {
        Row: {
          id: string;
          user_id: string;
          subscription_id: string;
          amount_cents: number;
          currency: string;
          status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
          invoice_number: string | null;
          invoice_date: string;
          due_date: string | null;
          paid_at: string | null;
          stripe_invoice_id: string | null;
          stripe_payment_intent_id: string | null;
          invoice_pdf_url: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          subscription_id: string;
          amount_cents: number;
          currency?: string;
          status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
          invoice_number?: string | null;
          invoice_date: string;
          due_date?: string | null;
          paid_at?: string | null;
          stripe_invoice_id?: string | null;
          stripe_payment_intent_id?: string | null;
          invoice_pdf_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          subscription_id?: string;
          amount_cents?: number;
          currency?: string;
          status?: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
          invoice_number?: string | null;
          invoice_date?: string;
          due_date?: string | null;
          paid_at?: string | null;
          stripe_invoice_id?: string | null;
          stripe_payment_intent_id?: string | null;
          invoice_pdf_url?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      subscription_usage: {
        Row: {
          id: string;
          user_id: string;
          subscription_id: string;
          period_start: string;
          period_end: string;
          credits_included: number;
          credits_used: number;
          credits_remaining: number;
          overage_credits: number;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          subscription_id: string;
          period_start: string;
          period_end: string;
          credits_included: number;
          credits_used?: number;
          credits_remaining: number;
          overage_credits?: number;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          subscription_id?: string;
          period_start?: string;
          period_end?: string;
          credits_included?: number;
          credits_used?: number;
          credits_remaining?: number;
          overage_credits?: number;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_style_preferences: {
        Row: {
          id: string;
          user_id: string;
          base_style_id: string;
          is_favorite: boolean;
          notes: string | null;
          usage_count: number;
          last_used_at: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          base_style_id: string;
          is_favorite?: boolean;
          notes?: string | null;
          usage_count?: number;
          last_used_at?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          base_style_id?: string;
          is_favorite?: boolean;
          notes?: string | null;
          usage_count?: number;
          last_used_at?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      staging_presets: {
        Row: {
          id: string;
          user_id: string | null;
          name: string;
          description: string | null;
          is_system: boolean;
          structure_type_id: string | null;
          base_style_id: string | null;
          custom_prompt_additions: string | null;
          furniture_overrides: Record<string, unknown>;
          decor_overrides: Record<string, unknown>;
          density_override: 'minimal' | 'light' | 'medium' | 'full' | 'luxury' | null;
          is_favorite: boolean;
          tags: string[];
          usage_count: number;
          last_used_at: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id?: string | null;
          name: string;
          description?: string | null;
          is_system?: boolean;
          structure_type_id?: string | null;
          base_style_id?: string | null;
          custom_prompt_additions?: string | null;
          furniture_overrides?: Record<string, unknown>;
          decor_overrides?: Record<string, unknown>;
          density_override?: 'minimal' | 'light' | 'medium' | 'full' | 'luxury' | null;
          is_favorite?: boolean;
          tags?: string[];
          usage_count?: number;
          last_used_at?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string | null;
          name?: string;
          description?: string | null;
          is_system?: boolean;
          structure_type_id?: string | null;
          base_style_id?: string | null;
          custom_prompt_additions?: string | null;
          furniture_overrides?: Record<string, unknown>;
          decor_overrides?: Record<string, unknown>;
          density_override?: 'minimal' | 'light' | 'medium' | 'full' | 'luxury' | null;
          is_favorite?: boolean;
          tags?: string[];
          usage_count?: number;
          last_used_at?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      download_events: {
        Row: {
          id: string;
          user_id: string;
          image_id: string;
          project_id: string | null;
          style: string | null;
          structure_domain: 'interior' | 'exterior' | null;
          structure_category: string | null;
          structure_type: string | null;
          download_type: 'single' | 'batch' | 'zip';
          file_naming_format: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          image_id: string;
          project_id?: string | null;
          style?: string | null;
          structure_domain?: 'interior' | 'exterior' | null;
          structure_category?: string | null;
          structure_type?: string | null;
          download_type?: 'single' | 'batch' | 'zip';
          file_naming_format?: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          image_id?: string;
          project_id?: string | null;
          style?: string | null;
          structure_domain?: 'interior' | 'exterior' | null;
          structure_category?: string | null;
          structure_type?: string | null;
          download_type?: 'single' | 'batch' | 'zip';
          file_naming_format?: string;
          created_at?: string;
        };
      };
      staging_analytics: {
        Row: {
          id: string;
          style: string;
          total_stagings: number;
          positive_feedback: number;
          negative_feedback: number;
          total_downloads: number;
          avg_regenerations: number;
          last_updated: string;
          structure_domain: 'interior' | 'exterior';
          structure_category: string;
          structure_type: string;
        };
        Insert: {
          id?: string;
          style: string;
          total_stagings?: number;
          positive_feedback?: number;
          negative_feedback?: number;
          total_downloads?: number;
          avg_regenerations?: number;
          last_updated?: string;
          structure_domain?: 'interior' | 'exterior';
          structure_category?: string;
          structure_type?: string;
        };
        Update: {
          id?: string;
          style?: string;
          total_stagings?: number;
          positive_feedback?: number;
          negative_feedback?: number;
          total_downloads?: number;
          avg_regenerations?: number;
          last_updated?: string;
          structure_domain?: 'interior' | 'exterior';
          structure_category?: string;
          structure_type?: string;
        };
      };
      listing_imports: {
        Row: {
          id: string;
          user_id: string;
          project_id: string | null;
          source: 'zillow' | 'streeteasy' | 'redfin' | 'realtor' | 'mls' | 'other';
          source_url: string;
          source_listing_id: string | null;
          address_street: string | null;
          address_city: string | null;
          address_state: string | null;
          address_zip: string | null;
          address_full: string | null;
          listing_price: number | null;
          bedrooms: number | null;
          bathrooms: number | null;
          sqft: number | null;
          property_type: string | null;
          listing_status: string | null;
          images_found: number;
          images_imported: number;
          import_status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
          error_message: string | null;
          raw_response: Record<string, unknown> | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          project_id?: string | null;
          source: 'zillow' | 'streeteasy' | 'redfin' | 'realtor' | 'mls' | 'other';
          source_url: string;
          source_listing_id?: string | null;
          address_street?: string | null;
          address_city?: string | null;
          address_state?: string | null;
          address_zip?: string | null;
          address_full?: string | null;
          listing_price?: number | null;
          bedrooms?: number | null;
          bathrooms?: number | null;
          sqft?: number | null;
          property_type?: string | null;
          listing_status?: string | null;
          images_found?: number;
          images_imported?: number;
          import_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
          error_message?: string | null;
          raw_response?: Record<string, unknown> | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          project_id?: string | null;
          source?: 'zillow' | 'streeteasy' | 'redfin' | 'realtor' | 'mls' | 'other';
          source_url?: string;
          source_listing_id?: string | null;
          address_street?: string | null;
          address_city?: string | null;
          address_state?: string | null;
          address_zip?: string | null;
          address_full?: string | null;
          listing_price?: number | null;
          bedrooms?: number | null;
          bathrooms?: number | null;
          sqft?: number | null;
          property_type?: string | null;
          listing_status?: string | null;
          images_found?: number;
          images_imported?: number;
          import_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
          error_message?: string | null;
          raw_response?: Record<string, unknown> | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      base_styles: {
        Row: {
          id: string;
          slug: string;
          name: string;
          description: string;
          prompt_template: string;
          preview_image_url: string | null;
          color_palette: string[];
          best_for: string[];
          /** @deprecated Use supported_domains/supported_categories instead */
          recommended_rooms: string[];
          /** Array of domain slugs this style supports (e.g., ["interior", "exterior"]) */
          supported_domains: string[];
          /** Array of category slugs - empty means all categories in supported domains */
          supported_categories: string[];
          /** Array of structure_type slugs to explicitly exclude */
          excluded_structure_types: string[];
          sort_order: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          slug: string;
          name: string;
          description: string;
          prompt_template: string;
          preview_image_url?: string | null;
          color_palette?: string[];
          best_for?: string[];
          recommended_rooms?: string[];
          supported_domains?: string[];
          supported_categories?: string[];
          excluded_structure_types?: string[];
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          slug?: string;
          name?: string;
          description?: string;
          prompt_template?: string;
          preview_image_url?: string | null;
          color_palette?: string[];
          best_for?: string[];
          recommended_rooms?: string[];
          supported_domains?: string[];
          supported_categories?: string[];
          excluded_structure_types?: string[];
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      user_styles: {
        Row: {
          id: string;
          user_id: string;
          name: string;
          description: string | null;
          prompt_template: string;
          preview_image_url: string | null;
          color_palette: string[];
          generation_prompt: string | null;
          visibility: 'private' | 'public' | 'shared';
          usage_count: number;
          is_favorite: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          name: string;
          description?: string | null;
          prompt_template: string;
          preview_image_url?: string | null;
          color_palette?: string[];
          generation_prompt?: string | null;
          visibility?: 'private' | 'public' | 'shared';
          usage_count?: number;
          is_favorite?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          name?: string;
          description?: string | null;
          prompt_template?: string;
          preview_image_url?: string | null;
          color_palette?: string[];
          generation_prompt?: string | null;
          visibility?: 'private' | 'public' | 'shared';
          usage_count?: number;
          is_favorite?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      style_examples: {
        Row: {
          id: string;
          base_style_id: string;
          room_type: string;
          image_url: string;
          sort_order: number;
          created_at: string;
        };
        Insert: {
          id?: string;
          base_style_id: string;
          room_type: string;
          image_url: string;
          sort_order?: number;
          created_at?: string;
        };
        Update: {
          id?: string;
          base_style_id?: string;
          room_type?: string;
          image_url?: string;
          sort_order?: number;
          created_at?: string;
        };
      };
      // ============================================
      // Structure Hierarchy Tables
      // ============================================
      structure_domains: {
        Row: {
          id: string;
          slug: string;
          name: string;
          icon: string | null;
          sort_order: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          slug: string;
          name: string;
          icon?: string | null;
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          slug?: string;
          name?: string;
          icon?: string | null;
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      structure_categories: {
        Row: {
          id: string;
          domain_id: string;
          slug: string;
          name: string;
          icon: string | null;
          sort_order: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          domain_id: string;
          slug: string;
          name: string;
          icon?: string | null;
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          domain_id?: string;
          slug?: string;
          name?: string;
          icon?: string | null;
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      structure_types: {
        Row: {
          id: string;
          category_id: string;
          slug: string;
          name: string;
          description: string | null;
          icon: string | null;
          typical_dimensions: string | null;
          lighting_characteristics: string | null;
          key_features: string[];
          sort_order: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          category_id: string;
          slug: string;
          name: string;
          description?: string | null;
          icon?: string | null;
          typical_dimensions?: string | null;
          lighting_characteristics?: string | null;
          key_features?: string[];
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          category_id?: string;
          slug?: string;
          name?: string;
          description?: string | null;
          icon?: string | null;
          typical_dimensions?: string | null;
          lighting_characteristics?: string | null;
          key_features?: string[];
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      structure_staging_defaults: {
        Row: {
          id: string;
          structure_type_id: string;
          base_prompt: string;
          furniture_items: Record<string, unknown>[];
          decor_items: Record<string, unknown>[];
          lighting_adjustments: Record<string, unknown> | null;
          staging_density: 'minimal' | 'light' | 'medium' | 'full' | 'luxury';
          scale_guidance: string | null;
          recommended_styles: string[];
          incompatible_styles: string[];
          focal_point_guidance: string | null;
          avoid_zones: string[];
          seasonal_variants: Record<string, unknown> | null;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          structure_type_id: string;
          base_prompt: string;
          furniture_items?: Record<string, unknown>[];
          decor_items?: Record<string, unknown>[];
          lighting_adjustments?: Record<string, unknown> | null;
          staging_density?: 'minimal' | 'light' | 'medium' | 'full' | 'luxury';
          scale_guidance?: string | null;
          recommended_styles?: string[];
          incompatible_styles?: string[];
          focal_point_guidance?: string | null;
          avoid_zones?: string[];
          seasonal_variants?: Record<string, unknown> | null;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          structure_type_id?: string;
          base_prompt?: string;
          furniture_items?: Record<string, unknown>[];
          decor_items?: Record<string, unknown>[];
          lighting_adjustments?: Record<string, unknown> | null;
          staging_density?: 'minimal' | 'light' | 'medium' | 'full' | 'luxury';
          scale_guidance?: string | null;
          recommended_styles?: string[];
          incompatible_styles?: string[];
          focal_point_guidance?: string | null;
          avoid_zones?: string[];
          seasonal_variants?: Record<string, unknown> | null;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      staging_prompt_components: {
        Row: {
          id: string;
          slug: string;
          component_type: 'furniture' | 'decor' | 'lighting' | 'atmosphere' | 'camera' | 'negative';
          name: string;
          prompt_fragment: string;
          applicable_domains: string[];
          applicable_categories: string[];
          applicable_structure_types: string[];
          compatible_styles: string[];
          sort_order: number;
          is_active: boolean;
          created_at: string;
        };
        Insert: {
          id?: string;
          slug: string;
          component_type: 'furniture' | 'decor' | 'lighting' | 'atmosphere' | 'camera' | 'negative';
          name: string;
          prompt_fragment: string;
          applicable_domains?: string[];
          applicable_categories?: string[];
          applicable_structure_types?: string[];
          compatible_styles?: string[];
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
        };
        Update: {
          id?: string;
          slug?: string;
          component_type?: 'furniture' | 'decor' | 'lighting' | 'atmosphere' | 'camera' | 'negative';
          name?: string;
          prompt_fragment?: string;
          applicable_domains?: string[];
          applicable_categories?: string[];
          applicable_structure_types?: string[];
          compatible_styles?: string[];
          sort_order?: number;
          is_active?: boolean;
          created_at?: string;
        };
      };
      staging_presets: {
        Row: {
          id: string;
          user_id: string | null;
          name: string;
          description: string | null;
          is_system: boolean;
          structure_type_id: string | null;
          base_style_id: string | null;
          custom_prompt_additions: string | null;
          furniture_overrides: Record<string, unknown>;
          decor_overrides: Record<string, unknown>;
          density_override: 'minimal' | 'light' | 'medium' | 'full' | 'luxury' | null;
          is_favorite: boolean;
          tags: string[];
          usage_count: number;
          last_used_at: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id?: string | null;
          name: string;
          description?: string | null;
          is_system?: boolean;
          structure_type_id?: string | null;
          base_style_id?: string | null;
          custom_prompt_additions?: string | null;
          furniture_overrides?: Record<string, unknown>;
          decor_overrides?: Record<string, unknown>;
          density_override?: 'minimal' | 'light' | 'medium' | 'full' | 'luxury' | null;
          is_favorite?: boolean;
          tags?: string[];
          usage_count?: number;
          last_used_at?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string | null;
          name?: string;
          description?: string | null;
          is_system?: boolean;
          structure_type_id?: string | null;
          base_style_id?: string | null;
          custom_prompt_additions?: string | null;
          furniture_overrides?: Record<string, unknown>;
          decor_overrides?: Record<string, unknown>;
          density_override?: 'minimal' | 'light' | 'medium' | 'full' | 'luxury' | null;
          is_favorite?: boolean;
          tags?: string[];
          usage_count?: number;
          last_used_at?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
  };
};
